import React, { useState, useEffect } from 'react';
import { loadTestimonials, type TestimonialData } from '../../utils/content-loader';

const TestimonialsSection: React.FC = () => {
  // Initialize with a "getting started" message
  const defaultTestimonials: TestimonialData[] = [
    {
      order: 1,
      slug: 'getting-started',
      name: 'Paokhum Team',
      role: 'Web & CCTV Solutions',
      content: "We're just getting started. Real client stories will be added soon — maybe yours will be the first!",
      rating: 5
    }
  ];

  const [testimonials, setTestimonials] = useState<TestimonialData[]>(defaultTestimonials);
  const [isLoading] = useState(false); // Set to false since we have default data

  // Try to load testimonials from markdown files
  useEffect(() => {
    loadTestimonials().then((loadedTestimonials) => {
      console.log('Testimonials loaded in component:', loadedTestimonials);
      // Only update if we have real testimonials (not the getting started message)
      if (loadedTestimonials && loadedTestimonials.length > 0) {
        // Check if it's not our default "getting started" message
        const hasRealTestimonials = loadedTestimonials.some(t => t.slug !== 'getting-started');
        if (hasRealTestimonials) {
          setTestimonials(loadedTestimonials);
        }
      }
    }).catch((error) => {
      console.error('Failed to load testimonials:', error);
      // Keep using default testimonials
    });
  }, []);

  // Show loading state while testimonials are being loaded
  if (isLoading) {
    return (
      <section id="testimonials" className="relative py-24 md:py-32 card-backdrop">
        <div className="container relative mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-foreground/60">Loading testimonials...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="relative py-24 md:py-32 section-backdrop-light">

      <div className="section-grain" aria-hidden="true" />
      <div className="container relative mx-auto px-3 sm:px-4 lg:px-6">
        <div className="mb-16">
          <div className="flex items-center lg:hidden mb-3">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-2">[ 05 ]</span>
            <div className="w-20 border-t-2 border-foreground" />
          </div>
          <div className="hidden lg:flex flex-row items-baseline">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-6">[ 05 ]</span>
            <div>
              <div className="w-20 border-t-2 border-foreground mb-4"></div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">What Our Clients Say</h2>
            </div>
          </div>
          <div className="lg:hidden">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">What Our Clients Say</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 max-w-3xl mx-auto items-stretch">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="relative animate-on-scroll" data-delay={`${index * 0.1}s`}>
              {/* Decorative Quotation Mark outside the card corner */}
              <div className="absolute -top-4 left-2 md:-top-6 md:-left-4 z-10 pointer-events-none">
                <span className="font-display text-7xl md:text-8xl text-glow">“</span>
              </div>

              {/* The card */}
              <div className="group glow-card card-backdrop border-2 border-foreground rounded-none px-6 md:px-8 py-5 md:py-6 transition-transform duration-200 hover:scale-[1.02] h-full min-h-[220px] md:min-h-[240px] flex flex-col justify-between">
                <div className="relative">
                  <p className="text-base md:text-lg lg:text-xl font-bold leading-snug text-foreground mb-4">
                    {testimonial.content}
                  </p>
                  <div className="text-right">
                    <p className="font-display font-bold text-sm md:text-base text-foreground uppercase">{testimonial.name}</p>
                    <p className="text-xs md:text-sm text-foreground/70">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;

